import {
  FailedDestinationEntityRecord,
  IDestinationEntity,
  IDestinationEntityRecord,
  IGetRecordResponse,
  SkippedDestinationEntityRecord,
  type IPracticeMigration,
} from '@principle-theorem/practice-migrations/interfaces';
import { IPatient } from '@principle-theorem/principle-core/interfaces';
import {
  DocumentReference,
  WithRef,
  resolveSequentially,
} from '@principle-theorem/shared';
import { compact } from 'lodash';
import { PATIENT_RESOURCE_TYPE } from '../../../destination/entities/patient';
import {
  PatientDocumentDestinationEntity as ExactPatientDocumentsDestinationEntity,
  IPatientDocumentJobData,
  IPatientFileMigrationData,
  IPatientFileSourceFileData,
} from '../../../exact/destination/entities/patient-documents';
import { ExactPatientDocument } from '../../../exact/source/entities/patient-documents';
import { getBucketStoragePath } from '../../../storage';
import { TranslationMapHandler } from '../../../translation-map';

enum DentrixDocumentType {
  Document = 'Document',
  Xray = 'XRay',
}

const DENTRIX_PATH_MAP: { [key in DentrixDocumentType]: string } = {
  [DentrixDocumentType.Document]: 'documents',
  [DentrixDocumentType.Xray]: 'images',
};

export class PatientDocumentDestinationEntity extends ExactPatientDocumentsDestinationEntity {
  override async buildMigrationData(
    migration: WithRef<IPracticeMigration>,
    _destinationEntity: WithRef<IDestinationEntity>,
    translationMap: TranslationMapHandler,
    data: IPatientDocumentJobData
  ): Promise<
    | IPatientFileMigrationData
    | (IDestinationEntityRecord & FailedDestinationEntityRecord)
    | (IDestinationEntityRecord & SkippedDestinationEntityRecord)
  > {
    const sourcePatientId = data.sourcePatient.data.patient_id.toString();
    const patientRef = await translationMap.getDestination<IPatient>(
      sourcePatientId,
      PATIENT_RESOURCE_TYPE
    );

    if (!patientRef) {
      return super._buildErrorResponse(
        data.sourcePatient,
        `Couldn't resolve patient`
      );
    }

    const sourceDocuments =
      await this.sourceEntities.patientDocuments.filterRecords(
        migration,
        'patientId',
        sourcePatientId
      );

    const documents = await this.getPatientFiles(
      migration,
      patientRef,
      sourceDocuments
    );

    if (documents.missingFiles.length) {
      return this.buildSkippedResponse(data);
    }

    return {
      sourcePatientId,
      patientRef,
      documents: documents.files,
    };
  }

  async getPatientFiles(
    migration: WithRef<IPracticeMigration>,
    patientRef: DocumentReference<IPatient>,
    documents: IGetRecordResponse<ExactPatientDocument>[]
  ): Promise<{
    files: IPatientFileSourceFileData[];
    missingFiles: IPatientFileSourceFileData[];
  }> {
    const copyStorage = this.getCopyStorage(
      getBucketStoragePath(migration.source),
      patientRef,
      migration.configuration.projectId,
      migration.configuration.destinationBucket
    );

    const missingFiles: IPatientFileSourceFileData[] = [];
    const files = await resolveSequentially(documents, async (document) => {
      const location =
        DENTRIX_PATH_MAP[document.data.type as DentrixDocumentType];
      const filePath = [location, document.data.source_path].join('/');

      const sourceFileExists = await copyStorage.getSourceFile(filePath);

      if (sourceFileExists) {
        return {
          sourceIdentifier: document.record.uid,
          name: document.data.title,
          sourceFilePath: filePath,
          createdAt: document.record.translations.date,
        };
      }

      missingFiles.push({
        sourceIdentifier: document.record.uid,
        name: document.data.title,
        sourceFilePath: filePath,
        createdAt: document.record.translations.date,
      });
    });

    return { files: compact(files), missingFiles };
  }
}
